import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/Toast';
import { formatCurrency } from '../utils/currency';
import './FinancialTransactions.css';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

function VendorPurchases({ isReturn = false }) {
  const { isCompanyAdmin } = useAuth();
  const [formData, setFormData] = useState({
    transactionType: isReturn ? 'PURCHASE_RETURN' : 'PURCHASE',
    transactionDate: new Date().toISOString().split('T')[0],
    branchId: '',
    vendorId: '',
    projectId: '',
    customerId: '',
    warehouseId: '',
    destinationType: 'PROJECT', // 'PROJECT' or 'WAREHOUSE'
    accountHeadId: '',
    paymentAccountHeadId: '',
    amount: '',
    taxAmount: '',
    referenceNo: '',
    dueDate: '',
    notes: '',
    paidAmount: '',
    paymentMode: 'CASH',
    paymentReferenceNo: ''
  });

  const [branches, setBranches]         = useState([]);
  const [warehouses, setWarehouses]     = useState([]);
  const [vendors, setVendors]           = useState([]);
  const [customers, setCustomers]       = useState([]);
  const [projects, setProjects]         = useState([]);
  const [allAccountHeads, setAllAccountHeads] = useState([]);
  const accountHeads = useMemo(() => {
    const categoryToMatch = isReturn ? 'INCOME' : 'EXPENSE';
    return allAccountHeads.filter(h => h.category === categoryToMatch);
  }, [allAccountHeads, isReturn]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [editingTxn, setEditingTxn]           = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [listLoading, setListLoading]   = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [assocPaymentId, setAssocPaymentId] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [viewingTxn, setViewingTxn] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const purchasesPerPage = 10;
  const [showNewAccountHeadModal, setShowNewAccountHeadModal] = useState(false);
  const [newAccountHeadForm, setNewAccountHeadForm] = useState({
    name: '',
    description: ''
  });
  const [historyTxn, setHistoryTxn] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [items, setItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [newItemForm, setNewItemForm] = useState({
    itemCode: '',
    name: '',
    description: '',
    categoryId: '',
    unitOfMeasurement: 'PCS',
    purchasePrice: '',
    quantity: '0',
    minStockLevel: '0',
    inventoryType: 'MATERIAL'
  });
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    if (historyTxn) {
      fetchHistory(historyTxn.id);
    }
  }, [historyTxn]);

  const fetchHistory = async (txnId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/financial-transactions/${txnId}/approval-history`);
      setHistoryList(res.data?.data || []);
    } catch (err) {
      toast.error('Failed to load approval history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderStatus = (txn) => {
    const status = txn.status || 'APPROVED';
    let bg = '#e2e8f0', color = '#475569', label = status;
    if (status === 'APPROVED') { bg = '#dcfce7'; color = '#15803d'; label = 'Approved'; }
    else if (status === 'REJECTED') { bg = '#fee2e2'; color = '#b91c1c'; label = 'Rejected'; }
    else if (status === 'PENDING_L1') { bg = '#fef3c7'; color = '#d97706'; label = 'Pending L1'; }
    else if (status === 'PENDING_L2') { bg = '#f3e8ff'; color = '#7e22ce'; label = 'Pending L2'; }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
        <span 
          style={{ 
            backgroundColor: bg, 
            color: color, 
            padding: '2px 8px', 
            borderRadius: '4px', 
            fontSize: '11px', 
            fontWeight: '600',
            textTransform: 'uppercase',
            display: 'inline-block'
          }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={() => setHistoryTxn(txn)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '14px' }}
          title="View Approval History"
        >
          ℹ️
        </button>
      </div>
    );
  };

  const renderHistoryModal = () => {
    if (!historyTxn) return null;
    return (
      <div className="modal-overlay" style={{ zIndex: 1200, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setHistoryTxn(null)}>
        <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Approval History - {historyTxn.voucherNo}</h2>
            <button type="button" className="close-button" onClick={() => setHistoryTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
          </div>
          <div className="modal-body" style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
            {historyLoading ? (
              <div>Loading history...</div>
            ) : historyList.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No history logs found (Bypassed or legacy transaction).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {historyList.map((h, i) => {
                  let actColor = 'var(--text-main)';
                  if (h.action?.includes('APPROVE')) actColor = 'green';
                  if (h.action?.includes('REJECT')) actColor = 'red';
                  return (
                    <div key={h.id} style={{ display: 'flex', gap: '12px', borderBottom: i < historyList.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '10px' }}>
                      <div style={{ fontSize: '18px' }}>
                        {h.action?.includes('APPROVE') ? '✅' : h.action?.includes('REJECT') ? '❌' : '📤'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: actColor }}>{h.action}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{new Date(h.performedAt).toLocaleString()}</span>
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '2px' }}>
                          By: <strong>{h.performedByName}</strong> ({h.performedByRole})
                        </div>
                        {h.remarks && (
                          <div style={{ fontSize: '13px', background: '#f8f9fa', padding: '6px', borderRadius: '4px', marginTop: '4px', color: '#555', fontStyle: 'italic' }}>
                            Remarks: "{h.remarks}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryTxn(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };


  const fetchInventoryCategories = async () => {
    try {
      const response = await axios.get('/inventory/categories');
      const data = response.data.data || response.data || [];
      setInventoryCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching inventory categories:', error);
    }
  };

  const handleCreateInventoryItem = async () => {
    if (!newItemForm.itemCode.trim() || !newItemForm.name.trim() || !newItemForm.unitOfMeasurement.trim()) {
      alert('Item Code, Name, and Unit of Measurement are required.');
      return;
    }
    try {
      const payload = {
        itemCode: newItemForm.itemCode.trim(),
        name: newItemForm.name.trim(),
        description: newItemForm.description.trim(),
        categoryId: newItemForm.categoryId ? parseInt(newItemForm.categoryId) : null,
        unitOfMeasurement: newItemForm.unitOfMeasurement.trim(),
        purchasePrice: newItemForm.purchasePrice ? parseFloat(newItemForm.purchasePrice) : 0,
        quantity: newItemForm.quantity ? parseFloat(newItemForm.quantity) : 0,
        minStockLevel: newItemForm.minStockLevel ? parseFloat(newItemForm.minStockLevel) : 0,
        status: 'AVAILABLE',
        inventoryType: newItemForm.inventoryType || 'MATERIAL'
      };

      const response = await axios.post('/inventory', payload);
      const data = response.data?.data || response.data;
      if (data && data.id) {
        setInventoryItems(prev => [...prev, data]);
        await fetchInventoryItems();
        if (activeItemIndex !== null) {
          const newItems = [...items];
          newItems[activeItemIndex].inventoryItemId = String(data.id);
          newItems[activeItemIndex].unitPrice = String(data.purchasePrice || 0);
          setItems(newItems);
          
          const total = calculateTotalAmount(newItems);
          setFormData(prev => ({ ...prev, amount: total > 0 ? String(total.toFixed(2)) : '' }));
        }
      }
      setShowNewItemModal(false);
      setNewItemForm({
        itemCode: '',
        name: '',
        description: '',
        categoryId: '',
        unitOfMeasurement: 'PCS',
        purchasePrice: '',
        quantity: '0',
        minStockLevel: '0',
        inventoryType: 'MATERIAL'
      });
      setActiveItemIndex(null);
    } catch (err) {
      console.error('Failed to create inventory item:', err);
      alert(err.response?.data?.message || 'Failed to create inventory item');
    }
  };

  const handleCreateInventoryCategory = async () => {
    if (!newCategoryForm.name.trim()) return;
    try {
      const response = await axios.post('/inventory/categories', {
        name: newCategoryForm.name.trim(),
        description: newCategoryForm.description.trim()
      });
      const data = response.data?.data || response.data;
      if (data && data.id) {
        setInventoryCategories(prev => [...prev, data]);
        await fetchInventoryCategories();
        setNewItemForm(prev => ({
          ...prev,
          categoryId: String(data.id)
        }));
      }
      setShowNewCategoryModal(false);
      setNewCategoryForm({ name: '', description: '' });
    } catch (err) {
      console.error('Failed to create category:', err);
      alert(err.response?.data?.message || 'Failed to create category');
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const response = await axios.get('/inventory?size=1000');
      const data = response.data?.items || response.data?.data?.items || response.data?.data?.content || response.data?.content || response.data?.data || [];
      setInventoryItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  };

  const calculateTotalAmount = (itemArray) => {
    return itemArray.reduce((acc, curr) => {
      const q = parseFloat(curr.quantity) || 0;
      const p = parseFloat(curr.unitPrice) || 0;
      return acc + (q * p);
    }, 0);
  };

  const handleItemChange = (index, field, value) => {
    if (field === 'inventoryItemId' && (value === 'ADD_NEW_MATERIAL' || value === 'ADD_NEW_ASSET')) {
      setActiveItemIndex(index);
      setNewItemForm(prev => ({
        ...prev,
        inventoryType: value === 'ADD_NEW_ASSET' ? 'ASSET' : 'MATERIAL'
      }));
      setShowNewItemModal(true);
      return;
    }
    const newItems = [...items];
    newItems[index][field] = value;
    
    if (field === 'inventoryItemId') {
      const selectedItem = inventoryItems.find(i => String(i.id) === String(value));
      if (selectedItem) {
        newItems[index].unitPrice = String(selectedItem.purchasePrice || 0);
      }
      newItems[index].dropdownOpen = false;
      newItems[index].searchQuery = '';
    }
    
    setItems(newItems);
    
    const total = calculateTotalAmount(newItems);
    setFormData(prev => ({ ...prev, amount: total > 0 ? String(total.toFixed(2)) : '' }));
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data.data || response.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/vendors?size=1000');
      const data = response.data.data?.content || response.data.content || response.data.data || [];
      setVendors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };
  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/customers?size=1000');
      const data = response.data.data?.content || response.data.content || response.data.data || [];
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };
  const fetchProjects = async () => {
    try {
      const response = await axios.get('/projects?size=1000');
      const data = response.data.data?.content || response.data.content || response.data.data || [];
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get('/warehouses?activeOnly=true');
      const data = response.data.data || response.data || [];
      setWarehouses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchAccountHeads = async () => {
    try {
      const response = await axios.get('/account-heads');
      const data = response.data.data || response.data || [];
      setAllAccountHeads(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching account heads:', error);
    }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const res = await axios.get('/account-heads/category/PAYMENT_ACCOUNT');
      const data = res.data.data || res.data || [];
      setPaymentAccounts(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };



  const fetchRecentPurchases = useCallback(async () => {
    setListLoading(true);
    try {
      const typeToFetch = isReturn ? 'PURCHASE_RETURN' : 'PURCHASE';
      const response = await axios.get(`/financial-transactions/type/${typeToFetch}`);
      const data = response.data.data || response.data || [];
      setRecentPurchases(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching recent purchases:', error);
    } finally {
      setListLoading(false);
    }
  }, [isReturn]);

  const handleCreateAccountHead = async () => {
    if (!newAccountHeadForm.name.trim()) return;
    try {
      const response = await axios.post('/account-heads', {
        name: newAccountHeadForm.name.trim(),
        description: newAccountHeadForm.description.trim(),
        category: isReturn ? 'INCOME' : 'EXPENSE',
        active: true
      });
      const data = response.data?.data || response.data;
      if (data && data.id) {
        await fetchAccountHeads();
        setFormData(prev => ({
          ...prev,
          accountHeadId: String(data.id)
        }));
      }
      setShowNewAccountHeadModal(false);
      setNewAccountHeadForm({ name: '', description: '' });
    } catch (err) {
      console.error('Failed to create account head:', err);
      alert(err.response?.data?.message || 'Failed to create account head');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'accountHeadId' && value === 'ADD_NEW') {
      setShowNewAccountHeadModal(true);
      setFormData(prev => ({ ...prev, accountHeadId: '' }));
      return;
    }
    if (name === 'customerId') {
      setFormData(prev => {
        const next = { ...prev, customerId: value };
        if (prev.projectId) {
          const selectedProj = projects.find(p => String(p.id) === prev.projectId);
          if (selectedProj && value && String(selectedProj.customerId) !== value) {
            next.projectId = '';
          }
        }
        return next;
      });
      return;
    }
    if (name === 'paymentAccountHeadId') {
      const selectedAcc = paymentAccounts.find(a => String(a.id) === value);
      const isCash = selectedAcc?.name?.toLowerCase().includes('cash');
      setFormData(prev => ({
        ...prev,
        paymentAccountHeadId: value,
        paymentMode: isCash ? 'CASH' : 'BANK',
        paymentReferenceNo: isCash ? '' : prev.paymentReferenceNo
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = async (txn) => {
    setEditingTxn(txn);
    setFormData({
      transactionType:      txn.transactionType      || 'PURCHASE',
      transactionDate:      txn.transactionDate      || new Date().toISOString().split('T')[0],
      branchId:             txn.branchId             ? String(txn.branchId)             : '',
      vendorId:             txn.vendorId             ? String(txn.vendorId)             : '',
      projectId:            txn.projectId            ? String(txn.projectId)            : '',
      customerId:           txn.customerId           ? String(txn.customerId)           : '',
      warehouseId:          txn.warehouseId          ? String(txn.warehouseId)          : '',
      destinationType:      txn.warehouseId          ? 'WAREHOUSE'                      : 'PROJECT',
      accountHeadId:        txn.accountHeadId        ? String(txn.accountHeadId)        : '',
      paymentAccountHeadId: txn.paymentAccountHeadId ? String(txn.paymentAccountHeadId) : '',
      amount:               txn.amount               ? String(txn.amount)               : '',
      taxAmount:            txn.taxAmount            ? String(txn.taxAmount)            : '',
      referenceNo:          txn.referenceNo          || '',
      dueDate:              txn.dueDate              || '',
      notes:                txn.notes                || '',
      paidAmount:           '',
      paymentMode:          'CASH',
      paymentReferenceNo:   ''
    });
    setItems(txn.items ? txn.items.map(item => ({
      inventoryItemId: String(item.inventoryItemId),
      quantity: String(item.quantity),
      unitPrice: String(item.unitPrice),
      notes: item.notes || ''
    })) : []);
    setVendorSearch('');
    setCustomerSearch('');
    setAssocPaymentId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      const res = await axios.get('/financial-transactions/type/PAYMENT');
      const payments = res.data.data || res.data || [];
      const assocPayment = payments.find(p => p.notes && p.notes.includes(`Paid against Purchase Voucher: ${txn.voucherNo}`));
      if (assocPayment) {
        setFormData(prev => ({
          ...prev,
          paidAmount:           assocPayment.amount               ? String(assocPayment.amount)               : '',
          paymentAccountHeadId: assocPayment.paymentAccountHeadId ? String(assocPayment.paymentAccountHeadId) : '',
          paymentMode:          assocPayment.paymentMode          || 'CASH',
          paymentReferenceNo:   assocPayment.referenceNo          || ''
        }));
        setAssocPaymentId(assocPayment.id);
      }
    } catch (e) {
      console.error('Error fetching associated payment:', e);
    }
  };

  const handleCancelEdit = () => {
    setEditingTxn(null);
    setFormData({
      transactionType: 'PURCHASE',
      transactionDate: new Date().toISOString().split('T')[0],
      branchId: '', vendorId: '', projectId: '', customerId: '', warehouseId: '', destinationType: 'PROJECT', accountHeadId: '',
      paymentAccountHeadId: '', amount: '', taxAmount: '', referenceNo: '', dueDate: '', notes: '',
      paidAmount: '', paymentMode: 'CASH', paymentReferenceNo: ''
    });
    setVendorSearch('');
    setCustomerSearch('');
    setAssocPaymentId(null);
    setItems([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (parseFloat(formData.paidAmount) > 0 && !formData.paymentAccountHeadId) {
        toast.error('Please select a Payment Account for the paid amount');
        setLoading(false);
        return;
      }

      const isProject = formData.destinationType === 'PROJECT';
      const payload = {
        transactionType:      formData.transactionType,
        transactionDate:      formData.transactionDate,
        branchId:             formData.branchId             ? parseInt(formData.branchId)             : null,
        vendorId:             formData.vendorId             ? parseInt(formData.vendorId)             : null,
        projectId:            isProject && formData.projectId ? parseInt(formData.projectId) : null,
        customerId:           isProject && formData.customerId ? parseInt(formData.customerId) : null,
        warehouseId:          !isProject && formData.warehouseId ? parseInt(formData.warehouseId) : null,
        accountHeadId:        formData.accountHeadId        ? parseInt(formData.accountHeadId)        : null,
        paymentAccountHeadId: formData.paymentAccountHeadId ? parseInt(formData.paymentAccountHeadId) : null,
        amount:               String(formData.amount),
        taxAmount:            formData.taxAmount ? String(formData.taxAmount) : null,
        referenceNo:          formData.referenceNo          || null,
        dueDate:              formData.dueDate              || null,
        notes:                formData.notes                || null,
        items: items.map(item => ({
          inventoryItemId: parseInt(item.inventoryItemId),
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          notes: item.notes || null
        }))
      };

      if (!payload.branchId && !editingTxn && isCompanyAdmin()) {
        toast.error('Please select a branch');
        setLoading(false);
        return;
      }

      let txn;
      if (editingTxn) {
        const res = await axios.put(`/financial-transactions/${editingTxn.id}`, payload);
        txn = res.data.data || res.data;

        if (assocPaymentId) {
          if (parseFloat(formData.paidAmount) > 0) {
            const paymentPayload = {
              transactionType: isReturn ? 'INCOME' : 'PAYMENT',
              transactionDate: formData.transactionDate,
              branchId: payload.branchId,
              paymentMode: formData.paymentMode,
              amount: String(formData.paidAmount),
              referenceNo: formData.paymentReferenceNo || null,
              notes: isReturn
                ? `Refund received against Purchase Return Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Return Ref: ${formData.referenceNo}` : '')
                : `Paid against Purchase Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Bill No: ${formData.referenceNo}` : ''),
              projectId: payload.projectId,
              warehouseId: payload.warehouseId,
              accountHeadId: payload.accountHeadId,
              paymentAccountHeadId: parseInt(formData.paymentAccountHeadId),
              vendorId: payload.vendorId
            };
            await axios.put(`/financial-transactions/${assocPaymentId}`, paymentPayload);
            toast.success(isReturn ? `Purchase Return and associated Refund updated successfully!` : `Purchase and associated Payment updated successfully!`);
          } else {
            const paymentPayload = {
              transactionType: isReturn ? 'INCOME' : 'PAYMENT',
              transactionDate: formData.transactionDate,
              branchId: payload.branchId,
              paymentMode: formData.paymentMode,
              amount: '0.00',
              referenceNo: formData.paymentReferenceNo || null,
              notes: isReturn
                ? `Refund received against Purchase Return Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Return Ref: ${formData.referenceNo}` : '')
                : `Paid against Purchase Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Bill No: ${formData.referenceNo}` : ''),
              projectId: payload.projectId,
              warehouseId: payload.warehouseId,
              accountHeadId: payload.accountHeadId,
              paymentAccountHeadId: parseInt(formData.paymentAccountHeadId),
              vendorId: payload.vendorId
            };
            await axios.put(`/financial-transactions/${assocPaymentId}`, paymentPayload);
            toast.success(isReturn ? `Purchase Return updated and associated Refund set to 0.00!` : `Purchase updated and associated Payment set to 0.00!`);
          }
        } else if (parseFloat(formData.paidAmount) > 0) {
          const paymentPayload = {
            transactionType: isReturn ? 'INCOME' : 'PAYMENT',
            transactionDate: formData.transactionDate,
            branchId: payload.branchId,
            paymentMode: formData.paymentMode,
            amount: String(formData.paidAmount),
            referenceNo: formData.paymentReferenceNo || null,
            notes: isReturn
              ? `Refund received against Purchase Return Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Return Ref: ${formData.referenceNo}` : '')
              : `Paid against Purchase Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Bill No: ${formData.referenceNo}` : ''),
            projectId: payload.projectId,
            warehouseId: payload.warehouseId,
            accountHeadId: payload.accountHeadId,
            paymentAccountHeadId: parseInt(formData.paymentAccountHeadId),
            vendorId: payload.vendorId
          };
          const payRes = await axios.post('/financial-transactions', paymentPayload);
          const payTxn = payRes.data.data || payRes.data;
          toast.success(isReturn
            ? `Purchase Return updated (Voucher: ${txn.voucherNo}) & Refund recorded (Voucher: ${payTxn.voucherNo})!`
            : `Purchase updated (Voucher: ${txn.voucherNo}) & Payment recorded (Voucher: ${payTxn.voucherNo})!`);
        } else {
          toast.success(`Updated! Voucher: ${txn.voucherNo}`);
        }
        setEditingTxn(null);
        setAssocPaymentId(null);
      } else {
        const res = await axios.post('/financial-transactions', payload);
        txn = res.data.data || res.data;

        if (parseFloat(formData.paidAmount) > 0) {
          const paymentPayload = {
            transactionType: isReturn ? 'INCOME' : 'PAYMENT',
            transactionDate: formData.transactionDate,
            branchId: payload.branchId,
            paymentMode: formData.paymentMode,
            amount: String(formData.paidAmount),
            referenceNo: formData.paymentReferenceNo || null,
            notes: isReturn
              ? `Refund received against Purchase Return Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Return Ref: ${formData.referenceNo}` : '')
              : `Paid against Purchase Voucher: ${txn.voucherNo}` + (formData.referenceNo ? ` / Bill No: ${formData.referenceNo}` : ''),
            projectId: payload.projectId,
            warehouseId: payload.warehouseId,
            accountHeadId: payload.accountHeadId,
            paymentAccountHeadId: parseInt(formData.paymentAccountHeadId),
            vendorId: payload.vendorId
          };
          const payRes = await axios.post('/financial-transactions', paymentPayload);
          const payTxn = payRes.data.data || payRes.data;
          toast.success(isReturn
            ? `Purchase Return recorded (Voucher: ${txn.voucherNo}) & Refund recorded (Voucher: ${payTxn.voucherNo})!`
            : `Purchase recorded (Voucher: ${txn.voucherNo}) & Payment recorded (Voucher: ${payTxn.voucherNo})!`);
        } else {
          toast.success(isReturn ? `Purchase Return recorded! Voucher: ${txn.voucherNo}` : `Purchase recorded! Voucher: ${txn.voucherNo}`);
        }
      }

      setFormData({
        transactionType: isReturn ? 'PURCHASE_RETURN' : 'PURCHASE',
        transactionDate: new Date().toISOString().split('T')[0],
        branchId: '', vendorId: '', projectId: '', customerId: '', warehouseId: '', destinationType: 'PROJECT', accountHeadId: '',
        paymentAccountHeadId: '', amount: '', taxAmount: '', referenceNo: '', dueDate: '', notes: '',
        paidAmount: '', paymentMode: 'CASH', paymentReferenceNo: ''
      });
      setVendorSearch('');
      setCustomerSearch('');
      setItems([]);
      fetchRecentPurchases();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccountName = paymentAccounts.find(a => String(a.id) === formData.paymentAccountHeadId)?.name || '';
  const isCashAccount = selectedAccountName.toLowerCase().includes('cash');

  // Filter recent purchases based on search query
  const filteredPurchases = recentPurchases.filter(txn => {
    const vend = (txn.vendorName || '').toLowerCase();
    const vch = (txn.voucherNo || '').toLowerCase();
    const q = listSearch.toLowerCase();
    return vend.includes(q) || vch.includes(q);
  });

  // Calculate pagination details
  const indexOfLastPurchase = currentPage * purchasesPerPage;
  const indexOfFirstPurchase = indexOfLastPurchase - purchasesPerPage;
  const currentPurchases = filteredPurchases.slice(indexOfFirstPurchase, indexOfLastPurchase);
  const totalPages = Math.ceil(filteredPurchases.length / purchasesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      transactionType: isReturn ? 'PURCHASE_RETURN' : 'PURCHASE',
      branchId: '',
      vendorId: '',
      projectId: '',
      customerId: '',
      warehouseId: '',
      destinationType: 'PROJECT',
      accountHeadId: '',
      paymentAccountHeadId: '',
      amount: '',
      taxAmount: '',
      referenceNo: '',
      dueDate: '',
      notes: '',
      paidAmount: '',
      paymentMode: 'CASH',
      paymentReferenceNo: ''
    }));
    setEditingTxn(null);
    setItems([]);
  }, [isReturn]);

  useEffect(() => {
    if (isCompanyAdmin()) fetchBranches();
    fetchVendors();
    fetchCustomers();
    fetchProjects();
    fetchAccountHeads();
    fetchPaymentAccounts();
    fetchInventoryItems();
    fetchInventoryCategories();
    fetchWarehouses();
  }, []);

  useEffect(() => {
    fetchRecentPurchases();
  }, [isReturn, fetchRecentPurchases]);

  return (
    <div className="financial-transactions">
      <h1>{isReturn ? 'Purchase Returns' : 'Vendor Purchases'}</h1>
      <p>{isReturn ? 'Record and track vendor purchase returns' : 'Record vendor bills and purchases'}</p>

      {/* Centered stacked layout wrapper */}
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* ── Form ── */}
        <div className="transaction-form-container" style={{ width: '100%' }}>
          <h2 className="section-title">{editingTxn ? `Editing ${editingTxn.voucherNo}` : (isReturn ? 'New Purchase Return' : 'New Purchase Entry')}</h2>
          <form onSubmit={handleSubmit} className="transaction-form" style={{ maxWidth: '100%' }}>

            {/* Branch selector — only for company admin */}
            {isCompanyAdmin() && (
              <div className="form-row">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Branch *</label>
                  <select name="branchId" value={formData.branchId} onChange={handleChange} required>
                    <option value="">Select Branch</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
            )}



            <div className="form-row">
              <div className="form-group">
                <label>{isReturn ? 'Return Date *' : 'Bill Date *'}</label>
                <input type="date" name="transactionDate" value={formData.transactionDate} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>{isReturn ? 'Return Reference No' : 'Bill Number'}</label>
                <input type="text" name="referenceNo" value={formData.referenceNo} onChange={handleChange}
                  placeholder={isReturn ? "Return voucher / reference" : "Vendor invoice number"} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Vendor *</label>
                <input
                  type="text"
                  placeholder="Type to search & select vendor..."
                  value={vendorDropdownOpen ? vendorSearch : (vendors.find(v => String(v.id) === formData.vendorId)?.name || '')}
                  onFocus={() => {
                    setVendorSearch('');
                    setVendorDropdownOpen(true);
                  }}
                  onChange={(e) => setVendorSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setVendorDropdownOpen(false), 200)}
                  required
                  style={{ cursor: 'pointer' }}
                />
                {vendorDropdownOpen && (
                  <div className="custom-select-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: '6px',
                    boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}>
                    {vendors
                      .filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase()))
                      .map(v => (
                        <div
                          key={v.id}
                          className="custom-select-option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setFormData(prev => ({ ...prev, vendorId: String(v.id) }));
                            setVendorDropdownOpen(false);
                            setVendorSearch('');
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            borderBottom: '1px solid var(--border, #f0f0f0)',
                            backgroundColor: String(v.id) === formData.vendorId ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                            color: String(v.id) === formData.vendorId ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                          }}
                          onMouseEnter={(e) => {
                            if (String(v.id) !== formData.vendorId) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                          }}
                          onMouseLeave={(e) => {
                            if (String(v.id) !== formData.vendorId) e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          {v.name}
                        </div>
                      ))
                    }
                    {vendors.filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase())).length === 0 && (
                      <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                        No vendors found
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Purchase Destination *</label>
                <select name="destinationType" value={formData.destinationType} onChange={handleChange} required>
                  <option value="PROJECT">Project & Customer</option>
                  <option value="WAREHOUSE">Warehouse</option>
                </select>
              </div>
            </div>

            {formData.destinationType === 'PROJECT' ? (
              <>
                <div className="form-row">
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>Customer (Optional)</label>
                    <input
                      type="text"
                      placeholder="Type to search & select customer..."
                      value={customerDropdownOpen ? customerSearch : (customers.find(c => String(c.id) === formData.customerId)?.name || '')}
                      onFocus={() => {
                        setCustomerSearch('');
                        setCustomerDropdownOpen(true);
                      }}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      onBlur={() => setTimeout(() => setCustomerDropdownOpen(false), 200)}
                      style={{ cursor: 'pointer' }}
                    />
                    {customerDropdownOpen && (
                      <div className="custom-select-dropdown" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        background: 'var(--bg-card, #ffffff)',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: '6px',
                        boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        <div
                          className="custom-select-option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleChange({ target: { name: 'customerId', value: '' } });
                            setCustomerDropdownOpen(false);
                            setCustomerSearch('');
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            borderBottom: '1px solid var(--border, #f0f0f0)',
                            color: 'var(--text-secondary, #666)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          -- None / Select Customer --
                        </div>
                        {customers
                          .filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
                          .map(c => (
                            <div
                              key={c.id}
                              className="custom-select-option"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleChange({ target: { name: 'customerId', value: String(c.id) } });
                                setCustomerDropdownOpen(false);
                                setCustomerSearch('');
                              }}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                borderBottom: '1px solid var(--border, #f0f0f0)',
                                backgroundColor: String(c.id) === formData.customerId ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                                color: String(c.id) === formData.customerId ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                              }}
                              onMouseEnter={(e) => {
                                if (String(c.id) !== formData.customerId) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                              }}
                              onMouseLeave={(e) => {
                                if (String(c.id) !== formData.customerId) e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              {c.name}
                            </div>
                          ))
                        }
                        {customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                          <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                            No customers found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Project (Optional)</label>
                    <select name="projectId" value={formData.projectId} onChange={handleChange}>
                      <option value="">Select Project</option>
                      {projects
                        .filter(p => !formData.customerId || String(p.customerId) === formData.customerId)
                        .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{isReturn ? 'Income Head *' : 'Expense Head *'}</label>
                    <select name="accountHeadId" value={formData.accountHeadId} onChange={handleChange} required>
                      <option value="">{isReturn ? 'Select Income Head' : 'Select Expense Head'}</option>
                      {accountHeads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                      <option value="ADD_NEW">{isReturn ? '+ Add New Income Head' : '+ Add New Expense Head'}</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ visibility: 'hidden' }}>
                    <label>&nbsp;</label>
                    <select disabled><option></option></select>
                  </div>
                </div>
              </>
            ) : (
              <div className="form-row">
                <div className="form-group">
                  <label>Warehouse *</label>
                  <select name="warehouseId" value={formData.warehouseId} onChange={handleChange} required>
                    <option value="">Select Warehouse</option>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{isReturn ? 'Income Head *' : 'Expense Head *'}</label>
                  <select name="accountHeadId" value={formData.accountHeadId} onChange={handleChange} required>
                    <option value="">{isReturn ? 'Select Income Head' : 'Select Expense Head'}</option>
                    {accountHeads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                    <option value="ADD_NEW">{isReturn ? '+ Add New Income Head' : '+ Add New Expense Head'}</option>
                  </select>
                </div>
              </div>
            )}

            <div style={{ marginTop: '20px', marginBottom: '20px', borderTop: '1px solid var(--border, #eee)', paddingTop: '15px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', fontWeight: '600', color: 'var(--text-main)' }}>Purchased Items</h3>
              {items.map((item, idx) => (
                <div key={idx} className="form-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ flex: 3, marginBottom: 0, minWidth: '180px', position: 'relative' }}>
                    <label style={{ fontSize: '11px', marginBottom: '2px' }}>Item *</label>
                    <input
                      type="text"
                      placeholder="Type to search item..."
                      value={item.dropdownOpen ? (item.searchQuery ?? '') : (inventoryItems.find(i => String(i.id) === String(item.inventoryItemId)) ? `${inventoryItems.find(i => String(i.id) === String(item.inventoryItemId)).name} (${inventoryItems.find(i => String(i.id) === String(item.inventoryItemId)).itemCode})` : '')}
                      onFocus={() => {
                        const newItems = [...items];
                        newItems[idx].searchQuery = '';
                        newItems[idx].dropdownOpen = true;
                        setItems(newItems);
                      }}
                      onChange={(e) => {
                        const newItems = [...items];
                        newItems[idx].searchQuery = e.target.value;
                        setItems(newItems);
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          const newItems = [...items];
                          newItems[idx].dropdownOpen = false;
                          setItems(newItems);
                        }, 200);
                      }}
                      required
                      style={{ cursor: 'pointer' }}
                    />
                    {item.dropdownOpen && (
                      <div className="custom-select-dropdown" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        background: 'var(--bg-card, #ffffff)',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: '6px',
                        boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        <div
                          className="custom-select-option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleItemChange(idx, 'inventoryItemId', 'ADD_NEW_MATERIAL');
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'var(--primary-color, #0056b3)',
                            borderBottom: '1px solid var(--border, #f0f0f0)',
                            backgroundColor: 'transparent'
                          }}
                        >
                          + Add New Material
                        </div>
                        <div
                          className="custom-select-option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleItemChange(idx, 'inventoryItemId', 'ADD_NEW_ASSET');
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'var(--primary-color, #0056b3)',
                            borderBottom: '1px solid var(--border, #f0f0f0)',
                            backgroundColor: 'transparent'
                          }}
                        >
                          + Add New Asset
                        </div>
                        {inventoryItems
                          .filter(i => {
                            const query = (item.searchQuery ?? '').toLowerCase();
                            return i.name.toLowerCase().includes(query) || i.itemCode.toLowerCase().includes(query);
                          })
                          .map(i => (
                            <div
                              key={i.id}
                              className="custom-select-option"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleItemChange(idx, 'inventoryItemId', String(i.id));
                              }}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                borderBottom: '1px solid var(--border, #f0f0f0)',
                                backgroundColor: String(i.id) === String(item.inventoryItemId) ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                                color: String(i.id) === String(item.inventoryItemId) ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                              }}
                              onMouseEnter={(e) => {
                                if (String(i.id) !== String(item.inventoryItemId)) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                              }}
                              onMouseLeave={(e) => {
                                if (String(i.id) !== String(item.inventoryItemId)) e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              {i.name} ({i.itemCode})
                            </div>
                          ))
                        }
                        {inventoryItems.filter(i => {
                          const query = (item.searchQuery ?? '').toLowerCase();
                          return i.name.toLowerCase().includes(query) || i.itemCode.toLowerCase().includes(query);
                        }).length === 0 && (
                          <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                            No items found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0, minWidth: '70px' }}>
                    <label style={{ fontSize: '11px', marginBottom: '2px' }}>Qty *</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                      placeholder="0"
                      min="0.01"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1.5, marginBottom: 0, minWidth: '90px' }}>
                    <label style={{ fontSize: '11px', marginBottom: '2px' }}>Unit Price *</label>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(idx, 'unitPrice', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1.5, marginBottom: 0, minWidth: '90px' }}>
                    <label style={{ fontSize: '11px', marginBottom: '2px' }}>Line Total</label>
                    <input
                      type="text"
                      value={formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0))}
                      disabled
                      style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 3, marginBottom: 0, minWidth: '150px' }}>
                    <label style={{ fontSize: '11px', marginBottom: '2px' }}>Notes</label>
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => handleItemChange(idx, 'notes', e.target.value)}
                      placeholder="Item notes..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = items.filter((_, i) => i !== idx);
                      setItems(newItems);
                      const total = calculateTotalAmount(newItems);
                      setFormData(prev => ({ ...prev, amount: total > 0 ? String(total.toFixed(2)) : '' }));
                    }}
                    style={{
                      marginTop: '15px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#b91c1c',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setItems([...items, { inventoryItemId: '', quantity: '', unitPrice: '', notes: '' }])}
                style={{ marginTop: '5px', fontSize: '13px', padding: '6px 12px' }}
              >
                + Add Purchased Item
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Amount (Total) *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                  readOnly={items.length > 0}
                  style={items.length > 0 ? { backgroundColor: '#f5f5f5', fontWeight: 'bold' } : {}}
                />
                {items.length > 0 && (
                  <small style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>Auto-calculated from items total.</small>
                )}
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Tax Amount</label>
                <input type="number" name="taxAmount" value={formData.taxAmount} onChange={handleChange}
                  placeholder="0.00" step="0.01" min="0" />
              </div>
              <div className="form-group">
                {/* Empty block to align tax amount */}
              </div>
            </div>

            <h3 className="section-subtitle" style={{ gridColumn: '1 / -1', margin: '20px 0 10px 0', borderTop: '1px solid var(--border, #eee)', paddingTop: '15px', color: 'var(--text-secondary, #555)', fontSize: '14px', fontWeight: '600' }}>
              {isReturn ? 'Refund/Return Collection Details (Optional)' : 'Payment Details (Optional)'}
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>{isReturn ? 'Refund Amount' : 'Paid Amount'}</label>
                <input type="number" name="paidAmount" value={formData.paidAmount} onChange={handleChange}
                  placeholder="0.00" step="0.01" min="0" />
              </div>
              <div className="form-group">
                <label>{isReturn ? 'Refund Account ' : 'Payment Account '}{parseFloat(formData.paidAmount) > 0 && '*'}</label>
                <select name="paymentAccountHeadId" value={formData.paymentAccountHeadId} onChange={handleChange} required={parseFloat(formData.paidAmount) > 0}>
                  <option value="">Select Account</option>
                  {paymentAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>

            {parseFloat(formData.paidAmount) > 0 && !isCashAccount && (
              <div className="form-row">
                <div className="form-group">
                  <label>{isReturn ? 'Refund Mode' : 'Payment Mode'}</label>
                  <select name="paymentMode" value={formData.paymentMode} onChange={handleChange}>
                    <option value="BANK">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{isReturn ? 'Refund Ref No' : 'Payment Ref No'}</label>
                  <input type="text" name="paymentReferenceNo" value={formData.paymentReferenceNo} onChange={handleChange}
                    placeholder={isReturn ? "Transaction ID / Ref" : "Cheque No / TXN ID"} />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange}
                placeholder="Additional notes..." rows="3" />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving…' : editingTxn ? (isReturn ? 'Update Return' : 'Update Purchase') : (isReturn ? 'Record Return' : 'Record Purchase')}
              </button>
              {editingTxn && (
                <button type="button" className="btn-secondary" onClick={handleCancelEdit}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        {/* ── Recent Purchases Table ── */}
        <div className="transaction-form-container" style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <h2 className="section-title" style={{ margin: 0, border: 'none', padding: 0 }}>{isReturn ? 'Recent Purchase Returns' : 'Recent Purchases'}</h2>
            <input
              type="text"
              placeholder="🔍 Search by vendor or voucher..."
              value={listSearch}
              onChange={(e) => {
                setListSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: '300px', padding: '8px 12px', fontSize: '13.5px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
            />
          </div>

          {listLoading ? (
            <div className="loading">Loading…</div>
          ) : filteredPurchases.length === 0 ? (
            <div className="no-data-panel" style={{ textAlign: 'center', padding: '30px' }}>No matching purchases found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ overflowX: 'auto' }}>
                <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-card-alt)', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Voucher No</th>
                      {!isReturn && <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Type</th>}
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Date</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Vendor & Project</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Bill Reference</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Due Date</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPurchases.map(txn => (
                      <tr key={txn.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', fontFamily: 'monospace', fontWeight: 'bold' }}>{txn.voucherNo}</td>
                        {!isReturn && (
                          <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: txn.transactionType === 'PURCHASE_RETURN' ? 'rgba(220, 53, 69, 0.1)' : 'rgba(40, 167, 69, 0.1)',
                              color: txn.transactionType === 'PURCHASE_RETURN' ? '#dc3545' : '#28a745'
                            }}>
                              {txn.transactionType === 'PURCHASE_RETURN' ? 'Return' : 'Purchase'}
                            </span>
                          </td>
                        )}
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>{formatDate(txn.transactionDate)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                          <div style={{ fontWeight: '500' }}>{txn.vendorName || '—'}</div>
                          {txn.projectName && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Project: {txn.projectName}</div>}
                          {txn.warehouseName && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Warehouse: {txn.warehouseName}</div>}
                          {txn.customerName && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Cust: {txn.customerName}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                          <div>{txn.referenceNo || '—'}</div>
                          {txn.accountHeadName && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{txn.accountHeadName}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                          {txn.dueDate ? (
                            <span style={{ color: new Date(txn.dueDate) < new Date() ? '#dc3545' : 'inherit' }}>
                              {formatDate(txn.dueDate)}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', fontWeight: 'bold', textAlign: 'right', color: 'var(--accent)' }}>{formatCurrency(txn.amount)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', textAlign: 'center' }}>{renderStatus(txn)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => setViewingTxn(txn)}
                              title="View Details"
                            >
                              👁 View
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => handleEdit(txn)}
                              disabled={txn.status === 'PENDING_L1' || txn.status === 'PENDING_L2'}
                              title={txn.status?.startsWith('PENDING') ? 'Pending approval' : 'Edit'}
                            >
                              ✏️ Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '16px' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    style={{ padding: '6px 12px' }}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(page => (
                    <button
                      key={page}
                      type="button"
                      className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handlePageChange(page)}
                      style={{
                        padding: '6px 12px',
                        background: currentPage === page ? 'var(--accent) !important' : 'transparent !important',
                        color: currentPage === page ? '#fff !important' : 'var(--text-primary) !important',
                        borderColor: currentPage === page ? 'var(--accent) !important' : 'var(--border) !important'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    style={{ padding: '6px 12px' }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── View Purchase Details Modal ── */}
        {viewingTxn && (
          <div className="modal-overlay" style={{ zIndex: 1100, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setViewingTxn(null)}>
            <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Purchase Details - {viewingTxn.voucherNo}</h2>
                <button type="button" className="close-button" onClick={() => setViewingTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
              </div>
              <div className="modal-body" style={{ padding: '24px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Voucher No</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{viewingTxn.voucherNo}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Date</span>
                    <span>{formatDate(viewingTxn.transactionDate)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Vendor</span>
                    <span>{viewingTxn.vendorName || '—'}</span>
                  </div>
                  {viewingTxn.customerName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Customer</span>
                      <span>{viewingTxn.customerName}</span>
                    </div>
                  )}
                  {viewingTxn.projectName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Project</span>
                      <span>{viewingTxn.projectName}</span>
                    </div>
                  )}
                  {viewingTxn.warehouseName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Warehouse</span>
                      <span>{viewingTxn.warehouseName}</span>
                    </div>
                  )}
                  {viewingTxn.accountHeadName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>
                        {viewingTxn.transactionType === 'PURCHASE_RETURN' ? 'Income Head' : 'Expense Head'}
                      </span>
                      <span>{viewingTxn.accountHeadName}</span>
                    </div>
                  )}
                  {viewingTxn.referenceNo && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Bill Number</span>
                      <span>{viewingTxn.referenceNo}</span>
                    </div>
                  )}
                  {viewingTxn.dueDate && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Due Date</span>
                      <span style={{ color: new Date(viewingTxn.dueDate) < new Date() ? '#dc3545' : 'inherit' }}>{formatDate(viewingTxn.dueDate)}</span>
                    </div>
                  )}
                  {viewingTxn.notes && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Notes</span>
                      <span style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{viewingTxn.notes}</span>
                    </div>
                  )}
                  {viewingTxn.items && viewingTxn.items.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Purchased Items</span>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                            <th style={{ padding: '6px' }}>Item</th>
                            <th style={{ padding: '6px', textAlign: 'right' }}>Qty</th>
                            <th style={{ padding: '6px', textAlign: 'right' }}>Price</th>
                            <th style={{ padding: '6px', textAlign: 'right' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewingTxn.items.map((item) => (
                            <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '6px' }}>{item.inventoryItemName} ({item.inventoryItemCode})</td>
                              <td style={{ padding: '6px', textAlign: 'right' }}>{item.quantity}</td>
                              <td style={{ padding: '6px', textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                              <td style={{ padding: '6px', textAlign: 'right' }}>{formatCurrency(item.totalAmount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'var(--text-primary)' }}>Amount</span>
                    <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'var(--accent)' }}>{formatCurrency(viewingTxn.amount)}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#ffffff' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setViewingTxn(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      {showNewAccountHeadModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100 }}
          onClick={(e) => {
            e.stopPropagation();
            setShowNewAccountHeadModal(false);
            setNewAccountHeadForm({ name: '', description: '' });
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '450px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{isReturn ? 'New Income Head' : 'New Expense Head'}</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowNewAccountHeadModal(false);
                  setNewAccountHeadForm({ name: '', description: '' });
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Name *</label>
                <input
                  type="text"
                  value={newAccountHeadForm.name}
                  onChange={(e) => setNewAccountHeadForm(p => ({ ...p, name: e.target.value }))}
                  placeholder={isReturn ? "e.g., Purchase Return Income" : "e.g., Cement Purchase"}
                  required
                />
              </div>
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={newAccountHeadForm.description}
                  onChange={(e) => setNewAccountHeadForm(p => ({ ...p, description: e.target.value }))}
                  placeholder={isReturn ? "Income head description..." : "Expense head description..."}
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewAccountHeadModal(false);
                  setNewAccountHeadForm({ name: '', description: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateAccountHead}
                disabled={!newAccountHeadForm.name.trim()}
              >
                {isReturn ? 'Create Income Head' : 'Create Expense Head'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewItemModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
            setShowNewItemModal(false);
            setNewItemForm({
              itemCode: '',
              name: '',
              description: '',
              categoryId: '',
              unitOfMeasurement: 'PCS',
              purchasePrice: '',
              quantity: '0',
              minStockLevel: '0',
              inventoryType: 'MATERIAL'
            });
            setActiveItemIndex(null);
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-main)' }}>
                New {newItemForm.inventoryType === 'ASSET' ? 'Asset' : 'Material'} Item
              </h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowNewItemModal(false);
                  setNewItemForm({
                    itemCode: '',
                    name: '',
                    description: '',
                    categoryId: '',
                    unitOfMeasurement: 'PCS',
                    purchasePrice: '',
                    quantity: '0',
                    minStockLevel: '0',
                    inventoryType: 'MATERIAL'
                  });
                  setActiveItemIndex(null);
                }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', overflowY: 'auto', maxHeight: '400px' }}>
              <div className="form-row" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Item Code *</label>
                  <input
                    type="text"
                    value={newItemForm.itemCode}
                    onChange={(e) => setNewItemForm(p => ({ ...p, itemCode: e.target.value }))}
                    placeholder="e.g., CMT-50"
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Name *</label>
                  <input
                    type="text"
                    value={newItemForm.name}
                    onChange={(e) => setNewItemForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., Portland Cement"
                    required
                  />
                </div>
              </div>

              <div className="form-row" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Category</label>
                  <select
                    value={newItemForm.categoryId}
                    onChange={(e) => {
                      if (e.target.value === 'ADD_NEW_CATEGORY') {
                        setShowNewCategoryModal(true);
                      } else {
                        setNewItemForm(p => ({ ...p, categoryId: e.target.value }));
                      }
                    }}
                  >
                    <option value="">Select Category</option>
                    {inventoryCategories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    <option value="ADD_NEW_CATEGORY">+ Add New Category</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Unit *</label>
                  <input
                    type="text"
                    value={newItemForm.unitOfMeasurement}
                    onChange={(e) => setNewItemForm(p => ({ ...p, unitOfMeasurement: e.target.value }))}
                    placeholder="e.g., PCS, KG, BAGS"
                    required
                  />
                </div>
              </div>

              <div className="form-row" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Purchase Price</label>
                  <input
                    type="number"
                    value={newItemForm.purchasePrice}
                    onChange={(e) => setNewItemForm(p => ({ ...p, purchasePrice: e.target.value }))}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Min Stock Level</label>
                  <input
                    type="number"
                    value={newItemForm.minStockLevel}
                    onChange={(e) => setNewItemForm(p => ({ ...p, minStockLevel: e.target.value }))}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={newItemForm.description}
                  onChange={(e) => setNewItemForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Item details..."
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewItemModal(false);
                  setNewItemForm({
                    itemCode: '',
                    name: '',
                    description: '',
                    categoryId: '',
                    unitOfMeasurement: 'PCS',
                    purchasePrice: '',
                    quantity: '0',
                    minStockLevel: '0',
                    inventoryType: 'MATERIAL'
                  });
                  setActiveItemIndex(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateInventoryItem}
                disabled={!newItemForm.itemCode.trim() || !newItemForm.name.trim()}
              >
                Create Item
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewCategoryModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1200, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
            setShowNewCategoryModal(false);
            setNewCategoryForm({ name: '', description: '' });
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '400px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-main)' }}>New Inventory Category</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowNewCategoryModal(false);
                  setNewCategoryForm({ name: '', description: '' });
                }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Category Name *</label>
                <input
                  type="text"
                  value={newCategoryForm.name}
                  onChange={(e) => setNewCategoryForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Building Materials"
                  required
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={newCategoryForm.description}
                  onChange={(e) => setNewCategoryForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe category..."
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewCategoryModal(false);
                  setNewCategoryForm({ name: '', description: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateInventoryCategory}
                disabled={!newCategoryForm.name.trim()}
              >
                Create Category
              </button>
            </div>
          </div>
        </div>
      )}

      {renderHistoryModal()}
    </div>
  );
}

export default VendorPurchases;
